// ---------------------------------------------------------------------------
// Manifesto de recuperação: a parte PURA da rede de segurança.
//
// Regra da casa (modelo Revit/Word): o trabalho vive em memória durante
// a sessão e só sobrevive se o utilizador gravar ("Guardar sessão").
// Um crash não pode custar o trabalho, por isso o que os stores
// escrevem em localStorage/IndexedDB passa a ser tratado como um BUFFER
// DE RECUPERAÇÃO, não como uma biblioteca que reaparece sozinha.
//
// O manifesto é o selo desse buffer: existe enquanto houver trabalho
// POR GRAVAR. Ao arrancar:
//   - manifesto presente  -> perguntar "recuperar a sessão anterior?"
//   - manifesto ausente   -> não perguntar nada (e o buffer que lá
//     estiver é lixo de uma sessão já gravada ou descartada)
//
// Este ficheiro não toca em storage nenhum: recebe listas de chaves e
// contagens e devolve objectos. Toda a parte suja está em recovery.ts.
// ---------------------------------------------------------------------------

/** Onde vive o manifesto. Chave de localStorage. */
export const RECOVERY_MANIFEST_KEY = 'vn2r.recovery.manifest'

/** Marca de "este browser já correu a versão nova". Serve para
 *  distinguir dados legados (que merecem migração e diálogo) de restos
 *  deixados por uma sessão já gravada (que são lixo). */
export const RECOVERY_MARKER_KEY = 'vn2r.recovery.v'
export const RECOVERY_MARKER_VALUE = '1'

export const RECOVERY_MANIFEST_VERSION = 1

export interface RecoveryCounts {
  /** IFCs com bytes em cache (IndexedDB `vn2r.ifcs`). */
  ifcs: number
  /** Projectos BCF abertos (IndexedDB `vn2r-bcf`). */
  bcfs: number
  /** Viewpoints locais (IndexedDB `vn2r.viewpoints`). */
  viewpoints: number
  /** Vistas 2D gravadas, somadas de todos os modelos. */
  views2d: number
  /** Rascunhos de autoria (IDS/IDSX). */
  drafts: number
  /** Conflitos com parecer de triagem (aceite / resolvido / com nota).
   *  OPCIONAL de proposito: manifestos gravados antes desta contagem
   *  existir continuam validos e as chamadas antigas a `buildManifest`
   *  continuam a compilar. Ausente conta zero em todo o lado. */
  clash?: number
}

export interface RecoveryManifest {
  version: number
  /** ISO da última vez que houve trabalho novo. */
  updatedAt: string
  counts: RecoveryCounts
  /** Nomes dos IFCs conhecidos, só para o diálogo dizer o que lá está.
   *  Pode vir vazio (migração de dados antigos não sabe os nomes). */
  ifcNames: string[]
  /** Chaves de localStorage envolvidas. Documenta o que o "descartar"
   *  vai apagar; nunca inclui preferências. */
  keys: string[]
}

export const EMPTY_COUNTS: RecoveryCounts = {
  ifcs: 0,
  bcfs: 0,
  viewpoints: 0,
  views2d: 0,
  drafts: 0,
  clash: 0,
}

// --- Chaves de TRABALHO em localStorage ------------------------------------
// Tudo o resto (~45 chaves de UI: tema, escala, larguras das barras,
// snap, modos de workspace, atalhos…) é PREFERÊNCIA e nunca é tocado.

/** Vistas 2D por modelo: `vn2r.views2d.<sha256>`. Cuidado com
 *  `vn2r.views2d.snap`, que é preferência de quem desenha e fica. */
const VIEWS2D_PREFIX = 'vn2r.views2d.'
const VIEWS2D_SNAP_KEY = 'vn2r.views2d.snap'

export const IDS_DRAFT_KEY = 'vn2r.ids.draft'
export const IDSX_DRAFT_KEY = 'vn2r.idsx.draft'

/** Triagem dos conflitos (aceite / resolvido / notas), indexada pelo
 *  `clashKey`. Os RESULTADOS da deteccao nao vivem aqui: sao derivados
 *  do modelo e da configuracao e ficam em memoria. */
export const CLASH_TRIAGE_KEY = 'vn2r.clash.triage'

/** Planos de corte, combinações e estilos do corte (regras por classe
 *  e material). O estilo base é preferência e não vem aqui. */
export const CORTES_KEY = 'vn2r.cortes'

/** Rascunhos de autoria: um por formato. */
const DRAFT_KEYS = [IDS_DRAFT_KEY, IDSX_DRAFT_KEY]

export function isViews2dKey(key: string): boolean {
  if (!key.startsWith(VIEWS2D_PREFIX)) return false
  if (key === VIEWS2D_SNAP_KEY) return false
  // O sufixo é um SHA-256 em hexadecimal. Aceita-se qualquer hex com
  // pelo menos 6 dígitos para não ficar refém do comprimento.
  return /^[0-9a-f]{6,}$/i.test(key.slice(VIEWS2D_PREFIX.length))
}

export function isDraftKey(key: string): boolean {
  return DRAFT_KEYS.includes(key)
}

export function isClashTriageKey(key: string): boolean {
  return key === CLASH_TRIAGE_KEY
}

export function isCortesKey(key: string): boolean {
  return key === CORTES_KEY
}

/** Verdadeiro só para chaves de TRABALHO. Uma preferência que caia aqui
 *  por engano seria apagada no "descartar", por isso a lista é
 *  explícita e conservadora. */
export function isWorkKey(key: string): boolean {
  return isViews2dKey(key) || isDraftKey(key) || isClashTriageKey(key) || isCortesKey(key)
}

export function collectWorkKeys(keys: string[]): string[] {
  return keys.filter(isWorkKey).sort()
}

/** Conta as vistas dentro de um ficheiro `vn2r.views2d.<sha>`. JSON
 *  estragado ou de outra versão conta zero, nunca rebenta. */
export function countViewsInRaw(raw: string | null): number {
  if (!raw) return 0
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return 0
    const views = (parsed as { views?: unknown }).views
    return Array.isArray(views) ? views.length : 0
  } catch {
    return 0
  }
}

/** Conta os pareceres dentro do ficheiro `vn2r.clash.triage`. JSON
 *  estragado ou de outra versão conta zero, nunca rebenta. */
export function countTriagedInRaw(raw: string | null): number {
  if (!raw) return 0
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return 0
    const entries = (parsed as { entries?: unknown }).entries
    if (!entries || typeof entries !== 'object') return 0
    return Object.keys(entries as Record<string, unknown>).length
  } catch {
    return 0
  }
}

export interface BuildManifestInput {
  counts: RecoveryCounts
  keys: string[]
  ifcNames?: string[]
  now?: Date
}

export function buildManifest(input: BuildManifestInput): RecoveryManifest {
  const names = Array.from(new Set(input.ifcNames ?? [])).slice(0, 20)
  return {
    version: RECOVERY_MANIFEST_VERSION,
    updatedAt: (input.now ?? new Date()).toISOString(),
    counts: { ...EMPTY_COUNTS, ...input.counts },
    ifcNames: names,
    keys: [...input.keys].sort(),
  }
}

export function manifestIsEmpty(m: RecoveryManifest | null): boolean {
  if (!m) return true
  const c = m.counts
  return (
    c.ifcs === 0 &&
    c.bcfs === 0 &&
    c.viewpoints === 0 &&
    c.views2d === 0 &&
    c.drafts === 0 &&
    (c.clash ?? 0) === 0
  )
}

/** Lê e valida o manifesto. Qualquer coisa estranha devolve null (o
 *  arranque segue como se não houvesse rede, que é o lado seguro:
 *  não perguntamos por trabalho que não sabemos descrever). */
export function parseManifest(raw: string | null): RecoveryManifest | null {
  if (!raw) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object') return null
  const o = parsed as Partial<RecoveryManifest>
  if (o.version !== RECOVERY_MANIFEST_VERSION) return null
  const c = o.counts as Partial<RecoveryCounts> | undefined
  if (!c || typeof c !== 'object') return null
  const num = (v: unknown): number =>
    typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0
  return {
    version: RECOVERY_MANIFEST_VERSION,
    updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : '',
    counts: {
      ifcs: num(c.ifcs),
      bcfs: num(c.bcfs),
      viewpoints: num(c.viewpoints),
      views2d: num(c.views2d),
      drafts: num(c.drafts),
      // Só se materializa quando o manifesto a traz: um manifesto
      // gravado antes desta contagem existir tem de continuar a ler-se
      // exactamente com os mesmos campos que tinha.
      ...(c.clash === undefined ? {} : { clash: num(c.clash) }),
    },
    ifcNames: Array.isArray(o.ifcNames)
      ? o.ifcNames.filter((n): n is string => typeof n === 'string')
      : [],
    keys: Array.isArray(o.keys)
      ? o.keys.filter((k): k is string => typeof k === 'string')
      : [],
  }
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/** Descrição em PT-PT do que está no buffer, para o diálogo. */
export function describeManifest(m: RecoveryManifest | null): string[] {
  if (!m) return []
  const out: string[] = []
  const c = m.counts
  if (c.ifcs > 0) out.push(plural(c.ifcs, 'IFC', 'IFCs'))
  if (c.bcfs > 0) out.push(plural(c.bcfs, 'BCF', 'BCFs'))
  if (c.views2d > 0) out.push(plural(c.views2d, 'vista 2D', 'vistas 2D'))
  if (c.viewpoints > 0) out.push(plural(c.viewpoints, 'viewpoint', 'viewpoints'))
  if (c.drafts > 0) out.push(plural(c.drafts, 'rascunho', 'rascunhos'))
  if ((c.clash ?? 0) > 0) {
    out.push(plural(c.clash ?? 0, 'conflito triado', 'conflitos triados'))
  }
  return out
}

/** Data do manifesto em formato curto PT-PT. Vazio quando não há. */
export function formatManifestDate(m: RecoveryManifest | null): string {
  if (!m || !m.updatedAt) return ''
  const d = new Date(m.updatedAt)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
